import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Text } from "@/components/ui/baseUi/text"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { cn } from "@/modules/lib/utils"
import type { SessionMediaItemEntity } from "@/modules/sessions/entities/sessionBlockMediaEntity"
import { MediaDetailsImage } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetailsImage"
import { MediaDetailsPdf } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetailsPdf"
import { MediaDetailsVideo } from "@/modules/sessions/sessionDetails/blocks/mediaBlock/components/mediaDetailsVideo"
import { word } from "@modules/core/utils/i18n"
import { VimeoPlayer } from "@modules/utils/components/vimeoVideo"
import { YouTubeVideoPlayer } from "@modules/utils/components/youTubeVideo"
import { convertYoutubeLinkToEmbed, isVimeoLink, isYoutubeLink } from "@modules/utils/mediaUtils"

type Props = {
  medias: SessionMediaItemEntity[]
  className?: string
} & React.ComponentProps<"div">

export function MediaDetails({ medias, className, ...props }: Props) {
  const { theme } = useTheme()

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-4xl border border-borderDefault p-3",
        theme !== Theme.OwnerDark && "bg-neutral-50",
        className,
      )}
      {...props}
    >
      <Text fontStyle="TS" className="text-textDefault-body">
        {word("sessionForm.blocks.media")}
      </Text>

      {medias.map((media, index) => (
        <div
          key={index}
          className={cn(
            "flex h-80 flex-col overflow-hidden rounded-4xl",
            (media.type === MediaType.S3_VIDEO ||
              media.type === MediaType.S3_IMAGE ||
              media.type === MediaType.S3_PDF) &&
              "border border-borderDefault",
            (media.type === MediaType.YOUTUBE || media.type === MediaType.VIMEO) && "items-center justify-center",
          )}
        >
          {media.type === MediaType.S3_VIDEO ? (
            <MediaDetailsVideo key={index} media={media} />
          ) : media.type === MediaType.S3_IMAGE ? (
            <MediaDetailsImage key={index} media={media} />
          ) : media.type === MediaType.S3_PDF ? (
            <MediaDetailsPdf key={index} media={media} />
          ) : media.type === MediaType.YOUTUBE && media.url && isYoutubeLink(media.url) ? (
            <YouTubeVideoPlayer
              key={index}
              link={convertYoutubeLinkToEmbed(media.url)}
              width={492}
              height={320}
              className="m-auto max-w-max overflow-hidden rounded-4xl"
            />
          ) : media.type === MediaType.VIMEO && media.url && isVimeoLink(media.url) ? (
            <VimeoPlayer
              key={index}
              id={media.id ?? index.toString()}
              link={media.url}
              width={492}
              height={320}
              className="m-auto max-w-max overflow-hidden rounded-4xl"
            />
          ) : (
            <Text fontStyle="BM" className="wrap-break-word" key={index}>
              {word("sessionForm.blocks.add_media.missing_error")}
            </Text>
          )}
        </div>
      ))}
    </div>
  )
}
